import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { Box, Chip, Typography } from '@mui/material';

import { LogLinkApi } from '../../../../../../../api';
import ModalBoxes from '../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../../common/general/Button';
import NotificationManager from '../../../../../../../common/notifications/NotificationManager';
import { UNLINK_LOG_RECORD } from '../../../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../../../services/auth';
import { onRequestError } from '../../../../../../../services/handlers';

import { LinkTableMenu } from './LinkTableMenu/LinkTableMenu';
import { ParticipantTable } from './ParticipantTable/ParticipantTable';
import { RowMenu } from './ParticipantTable/RowMenu/RowMenu';

import './LinkLogRecordModal.scss';

const LinkLogRecordModal = ({
  studyId,
  itemGroupTemplateId,
  itemGroupTemplateVersionId,
  rowId,
  patientInfo,
  ssuPatientId,
  modalBox
}) => {
  const [participant, setParticipant] = useState([]);
  const [linkedParticipant, setLinkedParticipant] = useState([]);

  const [rowSelectionModelForSelectedParticipant, setRowSelectionModelForSelectedParticipant] = useState({
    type: 'include',
    ids: new Set([])
  });
  const [rowSelectionModelForSelectedLinkedParticipant, setRowSelectionModelForSelectedLinkedParticipant] = useState({
    type: 'include',
    ids: new Set([])
  });

  const selectedParticipant = useMemo(() => {
    return Array.from(rowSelectionModelForSelectedParticipant.ids);
  }, [rowSelectionModelForSelectedParticipant]);

  const selectedLinkedParticipant = useMemo(() => {
    return Array.from(rowSelectionModelForSelectedLinkedParticipant.ids);
  }, [rowSelectionModelForSelectedLinkedParticipant]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
      modalBox.close();
    }
  }, [blocker, modalBox]);

  const getParticipant = useCallback(() => {
    LogLinkApi.getParticipants({ studyId, ssuPatientId, itemGroupTemplateId, itemGroupTemplateVersionId, rowId })
      .then(({ data }) => {
        const participants = data.filter(participant => !participant.linked);
        const linkedParticipants = data.filter(participant => participant.linked);
        setParticipant(participants);
        setLinkedParticipant(linkedParticipants);
      })
      .catch(onRequestError);
  }, [itemGroupTemplateId, itemGroupTemplateVersionId, rowId, ssuPatientId, studyId]);

  const linkParticipant = useCallback(
    selectedParticipants => {
      const participantsCount = selectedParticipants.length;
      ModalBoxes.confirm({
        title: 'Confirmation',
        content: `You are about to link this Protocol Deviation for Subject with a Patient ID of 
        ${patientInfo?.patientId || 'No Patient ID'} with ${participantsCount}
              other subject(s), which creates the same deviation record for them. Once created, the record cannot be
              deleted. Are you sure you want to proceed?`,
        confirmButton: 'Yes',
        cancelButton: 'No'
      }).then(
        () => {
          LogLinkApi.linkParticipants({
            studyId,
            ssuPatientId,
            itemGroupTemplateId,
            itemGroupTemplateVersionId,
            rowId,
            participants: selectedParticipants
          })
            .then(() => {
              NotificationManager.success(
                `Successfully linked this protocol deviation for ${participantsCount} subject(s)`
              );
              getParticipant();
            })
            .catch(() => {
              NotificationManager.error('A error has occurred when trying to link subject(s), please try again');
            });
        },
        () => {}
      );
    },
    [getParticipant, itemGroupTemplateId, itemGroupTemplateVersionId, rowId, ssuPatientId, studyId, patientInfo]
  );

  const unlinkParticipant = useCallback(
    selectedParticipants => {
      const participantsCount = selectedParticipants.length;
      ModalBoxes.confirm({
        title: 'Confirmation',
        content: `You are about to unlink the selected ${participantsCount} subject(s) for this protocol deviation. The unlinked patient’s deviation record will be canceled. Do you wish to continue?`,
        confirmButton: 'Yes',
        cancelButton: 'No'
      }).then(
        () => {
          LogLinkApi.unlinkParticipants({
            studyId,
            ssuPatientId,
            itemGroupTemplateId,
            itemGroupTemplateVersionId,
            rowId,
            participants: selectedParticipants
          })
            .then(() => {
              NotificationManager.success(
                `Successfully unlinked this protocol deviation for ${participantsCount} subject(s)`
              );
              getParticipant();
            })
            .catch(() => {
              NotificationManager.error('A error has occurred when trying to unlink subject(s), please try again');
            });
        },
        () => {}
      );
    },
    [getParticipant, itemGroupTemplateId, itemGroupTemplateVersionId, rowId, ssuPatientId, studyId]
  );

  useEffect(() => {
    getParticipant();
  }, [getParticipant]);

  const tableSectionStyles = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column'
    }),
    []
  );

  return (
    <ModalBoxes.Body>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, padding: '10px 30px 10px 30px', flexWrap: 'wrap' }}>
        <Chip
          data-testid="chip-log-info"
          size="small"
          label={`Study: ${patientInfo?.studyName ? patientInfo.studyName : 'N/A'}`}
        />
        <Chip
          data-testid="chip-log-info"
          size="small"
          label={`PCN: ${patientInfo?.studyPCN ? patientInfo.studyPCN : 'N/A'}`}
        />
        <Chip
          data-testid="chip-log-info"
          size="small"
          label={`Site: ${patientInfo?.siteName ? patientInfo.siteName : 'N/A'}`}
        />
        <Chip
          data-testid="chip-log-info"
          size="small"
          label={`Patient ID: ${patientInfo?.patientId ? patientInfo.patientId : 'N/A'}`}
        />
        <Chip
          data-testid="chip-log-info"
          size="small"
          label={`Subject ID: ${patientInfo?.subjectId ? patientInfo.subjectId : 'No Subject ID'}`}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', height: '100%' }}>
        <Typography sx={{ padding: '10px 30px 10px 30px' }} variant="h7">
          {`Linked Subject(s): ${linkedParticipant.length} | Selected to be Linked: ${selectedParticipant.length} | Selected to be Unlinked: ${selectedLinkedParticipant.length}`}
        </Typography>
        <Box sx={tableSectionStyles}>
          <Typography sx={{ paddingLeft: '40px' }} variant="h6">
            Review Subject(s) to be Linked
          </Typography>
          <LinkTableMenu selectedParticipant={selectedParticipant}>
            <Button
              priority="low"
              size="h28"
              onClick={() => linkParticipant(selectedParticipant)}
              data-testid="link-button"
            >
              Link Subject
            </Button>
          </LinkTableMenu>
          <ParticipantTable
            participant={participant}
            setSelectedParticipant={setRowSelectionModelForSelectedParticipant}
            data-testid="link-table"
            menuComponent={row => <RowMenu row={row} label="Link Subject" onOptionClickCallback={linkParticipant} />}
          />
        </Box>
        <Box sx={tableSectionStyles}>
          <Typography sx={{ paddingLeft: '40px' }} variant="h6">
            Linked Subject Log Record
          </Typography>
          <LinkTableMenu selectedParticipant={selectedLinkedParticipant}>
            <Button
              priority="low"
              size="h28"
              onClick={() => unlinkParticipant(selectedLinkedParticipant)}
              data-testid="unlink-button"
            >
              Unlink Subject
            </Button>
          </LinkTableMenu>
          <ParticipantTable
            participant={linkedParticipant}
            setSelectedParticipant={setRowSelectionModelForSelectedLinkedParticipant}
            data-testid="unlink-table"
            menuComponent={row => (
              <RowMenu row={row} label="Unlink Subject" onOptionClickCallback={unlinkParticipant} />
            )}
            canUserPerformActions={userHasAccessTo(UNLINK_LOG_RECORD)}
          />
        </Box>
      </Box>
    </ModalBoxes.Body>
  );
};

export default LinkLogRecordModal;
